/**
 * Small presentational atoms shared across the table rows, cards, and the
 * detail slide-over. Kept dumb: they take primitives, render Forge-styled
 * markup, and never touch the API.
 */
import { IconCheck, IconShield } from "./icons";
import { usd, usdShort, projectGlyph, type MemberStatus, type MemberVM } from "./memberModel";

const STATUS_LABEL: Record<MemberStatus, string> = {
  active: "active",
  pending: "pending",
};

export function Avatar({ m, size }: { m: MemberVM; size?: number }) {
  const cls = m.you ? "you" : m.status === "pending" ? "invited" : "";
  return (
    <span
      className={`uavatar ${cls}`.trim()}
      style={size ? { width: size, height: size } : undefined}
      aria-hidden="true"
    >
      {m.initials}
    </span>
  );
}

export function StatusPill({ status }: { status: MemberStatus }) {
  return (
    <span className={`ustatus ${status}`}>
      <span className="sdot" />
      {STATUS_LABEL[status]}
    </span>
  );
}

export function RoleBadge({ role }: { role: "admin" | "member" }) {
  return (
    <span className={`rolebadge ${role}`}>
      {role === "admin" ? <IconShield style={{ width: 11, height: 11 }} /> : null}
      {role === "admin" ? "Admin" : "Member"}
    </span>
  );
}

/**
 * Spend-vs-cap meter. A null cap means "unlimited": we render an empty track
 * and a "$X / ∞" label rather than a misleading 0%/100% bar.
 */
export function Meter({
  spentCents,
  capCents,
  inline,
}: {
  spentCents: number;
  capCents: number | null;
  inline?: boolean;
}) {
  const unlimited = capCents == null || capCents <= 0;
  const pct = unlimited ? 0 : Math.round((spentCents / capCents!) * 100);
  const over = !unlimited && pct >= 100;
  const cls = unlimited ? "ok" : over ? "over" : pct >= 85 ? "warn" : "ok";
  const width = Math.min(100, Math.max(0, pct));

  const label = unlimited
    ? `${usd(spentCents)} spent, no monthly cap`
    : `${usd(spentCents)} of ${usdShort(capCents!)} monthly cap, ${pct}%`;

  if (!inline) {
    return (
      <div className="umeter-track" role="img" aria-label={label}>
        <div className={`umeter-fill ${cls}`} style={{ width: width + "%" }} />
      </div>
    );
  }

  return (
    <div className="umeter-inline" role="img" aria-label={label} title={unlimited ? "no monthly cap" : `${pct}% of monthly cap`}>
      <div className="umeter-track">
        <div className={`umeter-fill ${cls}`} style={{ width: width + "%" }} />
      </div>
      <span className={`umeter-text${over ? " over" : ""}`} aria-hidden="true">
        {usd(spentCents)} / {unlimited ? "∞" : usdShort(capCents!)}
      </span>
    </div>
  );
}

export function Checkbox({
  on,
  mixed,
  onClick,
  label,
}: {
  on: boolean;
  mixed?: boolean;
  onClick: () => void;
  label?: string;
}) {
  return (
    <span
      className={`cbox${on ? " on" : ""}${mixed ? " mixed" : ""}`}
      role="checkbox"
      aria-checked={mixed ? "mixed" : on}
      aria-label={label}
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
    >
      {!mixed && <IconCheck />}
    </span>
  );
}

export function ProjectGlyphTile({ name, kind }: { name: string; kind: string }) {
  return <span className={`glyph glyph-${kind}`}>{projectGlyph(name)}</span>;
}
